import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const TASK_DETAIL_STATUS = {
  PENDING: "PENDING",
  DONE: "DONE",
  CANCEL: "CANCEL",
};

export const TASK_DETAIL_UI_STATUS = {
  ALL: "ALL",
  PENDING: "pending",
  DONE: "done",
};

export const getEntityId = (value) =>
  String(value?.id || value?._id || value || "");

export const getListRows = (payload) =>
  payload?.data ?? (Array.isArray(payload) ? payload : []);

export const getListTotal = (payload) =>
  Number(payload?.total ?? (Array.isArray(payload) ? payload.length : 0));

export const formatDate = (value, fallback = "") => {
  if (!value) return fallback;
  const date = dayjs(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : fallback;
};

export const parseDisplayDate = (value) => {
  if (!value) return null;
  const displayDate = dayjs(value, "DD/MM/YYYY", true);
  if (displayDate.isValid()) return displayDate;

  const apiDate = dayjs(value);
  return apiDate.isValid() ? apiDate : null;
};

export const getTaskStatus = (task) => {
  const endDate = parseDisplayDate(task?.endDate);
  if (!endDate) return "active";
  return endDate.endOf("day").isBefore(dayjs()) ? "ended" : "active";
};

export const isTaskPastDeadline = (task = {}) => {
  const endDate = parseDisplayDate(task.endDate);

  return Boolean(endDate && endDate.endOf("day").isBefore(dayjs()));
};

const getDisplayName = (value) =>
  value?.fullName ||
  value?.name ||
  value?.username ||
  value?.phone ||
  value?.title ||
  "";

export const getOfficerName = (detail) => {
  if (getDisplayName(detail?.officerInfo))
    return getDisplayName(detail.officerInfo);
  if (getDisplayName(detail?.officerId))
    return getDisplayName(detail.officerId);
  if (getDisplayName(detail?.user)) return getDisplayName(detail.user);

  if (typeof detail?.officer === "string" && detail.officer)
    return detail.officer;
  return detail?.officerName || "";
};

export const TASK_PRIORITY = {
  URGENT: "URGENT",
  IMPORTANT: "IMPORTANT",
  NORMAL: "NORMAL",
};

export const TASK_PRIORITY_OPTIONS = [
  { value: "URGENT", label: "Khẩn cấp", color: "red" },
  { value: "IMPORTANT", label: "Quan trọng", color: "orange" },
  { value: "NORMAL", label: "Bình thường", color: "blue" },
];

export const normalizeTask = (task = {}, metrics = {}) => {
  const id = getEntityId(task);
  const authorName =
    getDisplayName(task.authorId) ||
    getDisplayName(task.author) ||
    task.createdBy ||
    "";
  const total = Number(metrics.total ?? task.total ?? 0);
  const done = Number(metrics.done ?? task.done ?? 0);
  const rawStatus = String(task.status || "").toUpperCase();
  const statusAliases = {
    ACTIVE: "IN_PROGRESS",
    IN_PROGRESS: "IN_PROGRESS",
    COMPLETED: "COMPLETED",
    CANCEL: "CANCELLED",
    CANCELED: "CANCELLED",
    CANCELLED: "CANCELLED",
    PAUSE: "PAUSED",
    PAUSED: "PAUSED",
  };
  const manualStatus = statusAliases[rawStatus] || rawStatus;
  const isComplete = total > 0 && done >= total;

  let status;
  if (["CANCELLED", "PAUSED"].includes(manualStatus)) {
    status = manualStatus;
  } else if (manualStatus === "COMPLETED" || isComplete) {
    status = "COMPLETED";
  } else if (isTaskPastDeadline(task)) {
    status = "OVERDUE";
  } else if (manualStatus === "IN_PROGRESS") {
    status = "IN_PROGRESS";
  } else {
    const now = dayjs();
    const start = parseDisplayDate(task.startDate);

    if (start && start.startOf("day").isAfter(now)) {
      status = "NOT_STARTED";
    } else {
      status = "IN_PROGRESS";
    }
  }

  return {
    ...task,
    id,
    priority: task.priority || "NORMAL",
    startDate: formatDate(task.startDate),
    endDate: formatDate(task.endDate),
    createdAt: formatDate(task.createdAt),
    updatedAt: formatDate(task.updatedAt),
    total,
    done,
    status,
    createdBy: authorName,
  };
};

export const normalizeTaskDetailStatus = (status) => {
  if (status === TASK_DETAIL_STATUS.DONE || status === "done") {
    return TASK_DETAIL_UI_STATUS.DONE;
  }
  if (status === TASK_DETAIL_STATUS.CANCEL || status === "cancel") {
    return TASK_DETAIL_UI_STATUS.CANCEL;
  }
  return TASK_DETAIL_UI_STATUS.PENDING;
};

export const toApiTaskDetailStatus = (status) => {
  if (status === TASK_DETAIL_UI_STATUS.ALL || status === "ALL") {
    return "ALL";
  }
  if (
    status === TASK_DETAIL_UI_STATUS.DONE ||
    status === TASK_DETAIL_STATUS.DONE
  ) {
    return TASK_DETAIL_STATUS.DONE;
  }
  if (
    status === TASK_DETAIL_UI_STATUS.CANCEL ||
    status === TASK_DETAIL_STATUS.CANCEL
  ) {
    return TASK_DETAIL_STATUS.CANCEL;
  }
  return TASK_DETAIL_STATUS.PENDING;
};

export const normalizeTaskDetail = (detail = {}) => {
  const officerId = getEntityId(detail.officerId);
  const tdpId = getEntityId(detail.tdp);

  return {
    ...detail,
    id: getEntityId(detail),
    taskManagementId: getEntityId(detail.taskManagementId),
    officerId,
    officer:
      getOfficerName(detail) || (officerId ? `CB ${officerId.slice(-6)}` : ""),
    tdpId,
    tdp:
      detail.tdp?.name ||
      (typeof detail.tdp === "string" ? detail.tdp : ""),
    status: normalizeTaskDetailStatus(detail.status),
    doneAt: detail.doneAt
      ? dayjs(detail.doneAt).isValid()
        ? dayjs(detail.doneAt).format("HH:mm DD/MM/YYYY")
        : null
      : null,
    createdAt: formatDate(detail.createdAt),
    updatedAt: formatDate(detail.updatedAt),
  };
};

export const toTaskPayload = (values = {}, currentUser) => {
  const payload = {
    name: values.name,
    description: values.description || "",
    categoryId: values.categoryId || null,
    priority: values.priority || "NORMAL",
    startDate: values.startDate
      ? dayjs(values.startDate).toISOString()
      : undefined,
    endDate: values.endDate ? dayjs(values.endDate).toISOString() : undefined,
  };

  return payload;
};
